import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  createPayoutsOnboardingLink,
  getPayoutsActivity,
  getPayoutsSummary,
  getPayoutsStripeStatus,
  startPayoutsConnect,
  syncPayoutsConnectStatus,
  type GenericEarningsSummary,
  type PayoutActivityItem,
  type PlatformFeeProgram,
  type StripePayoutStatus,
} from '../../../api/payouts'

function moneyFromCents(cents: number) {
  const value = Number.isFinite(cents) ? cents / 100 : 0
  return value.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

const REQUIREMENT_LABELS: Record<string, string> = {
  business_profile_mcc: 'Business category',
  business_profile_name: 'Business name',
  business_profile_url: 'Business website',
  business_type: 'Business type',
  company_tax_id: 'Tax ID',
  company_verification_document: 'Business verification document',
  external_account: 'Bank account or debit card',
  individual_address_city: 'City',
  individual_address_line1: 'Street address',
  individual_address_postal_code: 'Postal code',
  individual_dob_day: 'Date of birth (day)',
  individual_dob_month: 'Date of birth (month)',
  individual_dob_year: 'Date of birth (year)',
  individual_email: 'Email',
  individual_first_name: 'First name',
  individual_id_number: 'SSN / ID number',
  individual_last_name: 'Last name',
  individual_phone: 'Phone number',
  individual_verification_document: 'Identity document',
  tos_acceptance_date: 'Terms acceptance',
  tos_acceptance_ip: 'Terms acceptance IP',
}

function requirementLabel(code: string) {
  return REQUIREMENT_LABELS[code] || code.replaceAll('_', ' ')
}

function feePercentLabel(percent: string | null | undefined) {
  if (!percent) return '-'
  return `${percent}%`
}

function dateLabel(iso: string | null) {
  if (!iso) return '-'
  const value = new Date(iso)
  if (Number.isNaN(value.getTime())) return '-'
  return value.toLocaleDateString()
}

function usdLabelFromCents(value: number | null | undefined) {
  if (value == null || Number.isNaN(Number(value))) return '-'
  return moneyFromCents(Number(value))
}

export function PlatformFeeProgramCard({ program }: { program: PlatformFeeProgram | null }) {
  if (!program) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="text-sm text-white/70">Loading platform fee program...</div>
      </div>
    )
  }

  const tierRank: Record<string, number> = { T0: 0, T1: 1, T2: 2, T3: 3, T4: 4 }
  const activeTier = String(program.active?.tier || '')
  const targetTier = String(program.target_next?.tier || '')
  const eligibleTier = String(program.eligible?.tier || '')
  const isMaxTier = activeTier === 'T4' || (activeTier !== '' && activeTier === targetTier)
  const eligibleNow = !isMaxTier && (tierRank[eligibleTier] ?? -1) >= (tierRank[targetTier] ?? 999)
  const roadmap = (program.roadmap || []).slice(0, 3)
  const reputation = program.reputation
  const reputationDrivers = (reputation?.drivers || []).slice(0, 5)
  const reputationTooltip = reputationDrivers.length
    ? reputationDrivers.map((d) => `${d.label}: ${d.value ?? 'N/A'} (${d.status})`).join('\n')
    : 'No reputation drivers available yet.'
  const nextTierReqMet = (program.requirements || []).filter((row) => row.met).length
  const nextTierReqTotal = (program.requirements || []).length

  const renderRequirementRow = (row: any) => (
    <li key={row.key} className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="text-white/85">{row.label}</span>
        <span className={row.met ? 'text-emerald-300' : 'text-amber-200'}>{row.met ? 'Met' : 'Not met'}</span>
      </div>
      <div className="mt-1 text-xs text-white/60">
        {row.display_rule || `${row.current ?? 'N/A'} ${row.op || ''} ${row.target ?? ''}`.trim()}
        {row.display_current === 'N/A' || row.current == null ? (
          <span
            className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-white/20 text-[10px] text-white/60"
            title="Not enough data yet to evaluate this metric."
            aria-label="Not enough data yet to evaluate this metric."
          >
            ?
          </span>
        ) : null}
      </div>
    </li>
  )

  return (
    <div id="platform-fee-program" className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-lg font-semibold">Platform Fee Program</div>
        <span className="rounded-full border border-white/15 bg-black/30 px-2 py-1 text-xs text-white/80">
          {program.active.tier_name || '-'}
        </span>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-white/10 bg-black/20 p-3">
          <div className="mb-2 flex items-center gap-1 text-xs text-white/60">
            Reputation score
            <span className="cursor-help text-white/50" title={reputationTooltip} aria-label="What affects reputation score">
              ?
            </span>
          </div>
          <div className="text-base font-semibold text-white">{reputation?.score ?? 'N/A'}</div>
          <div className="text-xs text-white/60">Current platform fee (locked)</div>
          <div className="mt-1 text-xl font-bold text-emerald-200">{feePercentLabel(program.active.fee_percent)}</div>
          {program.active.locked_until ? (
            <div className="mt-1 text-xs text-white/60">Locked until {dateLabel(program.active.locked_until || null)}</div>
          ) : null}
          {program.active.grace_until ? (
            <div className="mt-1 text-xs text-amber-200">Grace until {dateLabel(program.active.grace_until || null)}</div>
          ) : null}
        </div>
        <div className="rounded-lg border border-white/10 bg-black/20 p-3">
          <div className="text-xs text-white/60">Next tier (target)</div>
          <div className="mt-1 text-sm font-semibold text-white">
            {program.target_next?.tier_name
              ? `${program.target_next.tier_name} (${feePercentLabel(program.target_next.fee_percent)})`
              : 'Top tier reached'}
          </div>
          <div className="mt-1 text-xs text-white/60">
            {isMaxTier
              ? 'Max tier reached'
              : eligibleNow
                ? 'Eligible now - will apply at next lock'
                : 'Preview - complete requirements to unlock'}
          </div>
          {nextTierReqTotal > 0 ? (
            <div className="mt-2 text-xs text-white/65">
              Progress: {nextTierReqMet} of {nextTierReqTotal} requirements met
            </div>
          ) : null}
        </div>
      </div>
      <div className="mt-4">
        <div className="text-sm font-semibold">Tier roadmap</div>
        <ul className="mt-2 space-y-2">
          {roadmap.map((step, idx) => (
            <li key={step.tier} className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm">
              <details open={idx === 0}>
                <summary className="cursor-pointer text-white/90">
                  Tier {step.tier_name} ({feePercentLabel(step.fee_percent)})
                </summary>
                <ul className="mt-2 space-y-2">
                  {(step.requirements || []).map(renderRequirementRow)}
                </ul>
              </details>
            </li>
          ))}
          {!roadmap.length && isMaxTier ? (
            <li className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-white/70">Max tier reached.</li>
          ) : null}
          {!roadmap.length && !isMaxTier ? (
            <li className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-white/70">Roadmap unavailable - refresh.</li>
          ) : null}
        </ul>
      </div>
    </div>
  )
}

type StripeAction = 'start' | 'resume'

type StripeRedirectModalProps = {
  open: boolean
  busy: boolean
  onCancel: () => void
  onContinue: () => void
}

function StripeRedirectModal({ open, busy, onCancel, onContinue }: StripeRedirectModalProps) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-xl border border-white/15 bg-[#0f1116] p-5 shadow-2xl">
        <h2 className="text-lg font-semibold text-white">Continue to Stripe</h2>
        <p className="mt-2 text-sm text-white/80">Stripe securely verifies your identity and banking details.</p>
        <p className="mt-1 text-sm text-white/80">This usually takes a few minutes.</p>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-white/75">
          <li>Pixeloop never sees your bank login.</li>
          <li>You will return here automatically.</li>
        </ul>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg border border-white/20 px-3 py-2 text-sm text-white/80 hover:bg-white/10 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onContinue}
            disabled={busy}
            className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white border border-sky-500/40 hover:bg-sky-500 disabled:opacity-60"
          >
            Continue to Stripe
          </button>
        </div>
      </div>
    </div>
  )
}

export function StripePayoutSetupCard({
  status,
  busy,
  onConnectStart,
  onResumeSetup,
}: {
  status: StripePayoutStatus | null
  busy: boolean
  onConnectStart: () => void
  onResumeSetup: () => void
}) {
  if (!status) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="text-sm text-white/70">Loading Stripe payout setup...</div>
      </div>
    )
  }

  const connected = Boolean(status.stripe_connected)
  const enabled = connected && Boolean(status.charges_enabled) && Boolean(status.payouts_enabled)
  const needsInfo = connected && !enabled

  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      {!connected ? (
        <div className="rounded-xl border border-sky-500/25 bg-sky-500/10 p-4">
          <div className="text-lg font-semibold text-sky-100">Connect Stripe to get paid</div>
          <div className="mt-1 text-sm text-sky-100/85">
            Pixeloop uses Stripe Connect for payouts. Connect a Stripe account to receive earnings from bookings and subscriptions.
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={onConnectStart}
              disabled={busy}
              className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white border border-sky-500/40 hover:bg-sky-500 disabled:opacity-60"
            >
              Connect Stripe
            </button>
          </div>
        </div>
      ) : null}

      {needsInfo ? (
        <div className="rounded-xl border border-rose-500/25 bg-rose-500/10 p-4">
          <div className="text-lg font-semibold text-rose-100">Stripe connected - action required</div>
          <div className="mt-1 text-sm text-rose-100/85">Stripe needs more information before payouts can be enabled.</div>
          {status.requirements_currently_due?.length ? (
            <ul className="mt-3 list-disc pl-5 text-sm text-rose-100/90 space-y-1">
              {status.requirements_currently_due.slice(0, 5).map((item) => (
                <li key={item}>{requirementLabel(item)}</li>
              ))}
            </ul>
          ) : null}
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={onResumeSetup}
              disabled={busy}
              className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white border border-rose-500/40 hover:bg-rose-500 disabled:opacity-60"
            >
              Resume Stripe setup
            </button>
          </div>
        </div>
      ) : null}

      {enabled ? (
        <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-4">
          <div className="text-lg font-semibold text-emerald-100">Stripe connected</div>
          <div className="mt-2 grid gap-2 text-sm text-emerald-100/90 sm:grid-cols-2">
            <div>Payouts: Enabled</div>
            <div>Charges: Enabled</div>
            <div>Country: {status.account_country || '-'}</div>
            <div>Currency: {(status.default_currency || '').toUpperCase() || '-'}</div>
            <div>Bank: {status.bank_last4 ? `**** ${status.bank_last4}` : '-'}</div>
          </div>
          {status.dashboard_url ? (
            <div className="mt-3">
              <a href={status.dashboard_url} target="_blank" rel="noreferrer" className="text-sm text-emerald-100 underline underline-offset-2">
                Manage Stripe
              </a>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export function EarningsSummaryCard({ summary }: { summary: GenericEarningsSummary | null }) {
  if (!summary) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="text-sm text-white/70">Loading earnings summary...</div>
      </div>
    )
  }
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="text-lg font-semibold">Earnings Summary</div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-white/10 bg-black/20 p-3">
          <div className="text-xs text-white/60">
            Available to withdraw
            {summary.available_to_withdraw_usd_cents == null ? <span className="ml-1 text-white/50" title="Coming soon">?</span> : null}
          </div>
          <div className="mt-1 text-lg font-semibold text-emerald-200">{usdLabelFromCents(summary.available_to_withdraw_usd_cents)}</div>
        </div>
        <div className="rounded-lg border border-white/10 bg-black/20 p-3">
          <div className="text-xs text-white/60">
            Pending
            {summary.pending_usd_cents == null ? <span className="ml-1 text-white/50" title="Coming soon">?</span> : null}
          </div>
          <div className="mt-1 text-lg font-semibold text-amber-200">{usdLabelFromCents(summary.pending_usd_cents)}</div>
        </div>
        <div className="rounded-lg border border-white/10 bg-black/20 p-3">
          <div className="text-xs text-white/60">Lifetime earned</div>
          <div className="mt-1 text-lg font-semibold text-white">{usdLabelFromCents(summary.lifetime_earned_usd_cents)}</div>
        </div>
        <div className="rounded-lg border border-white/10 bg-black/20 p-3">
          <div className="text-xs text-white/60">Last payout</div>
          <div className="mt-1 text-lg font-semibold text-white">
            {summary.last_payout.amount_usd_cents == null ? '-' : usdLabelFromCents(summary.last_payout.amount_usd_cents)}
          </div>
          <div className="mt-1 text-xs text-white/60">{dateLabel(summary.last_payout.created_at || null)}</div>
        </div>
      </div>
      <div className="mt-3 text-xs text-white/60">Includes bookings and subscriptions. Excludes gifts and wallet top-ups.</div>
    </div>
  )
}

export default function CreatorPayouts() {
  const location = useLocation()
  const navigate = useNavigate()

  const [stripeStatus, setStripeStatus] = useState<StripePayoutStatus | null>(null)
  const [summary, setSummary] = useState<GenericEarningsSummary | null>(null)
  const [activity, setActivity] = useState<PayoutActivityItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalAction, setModalAction] = useState<StripeAction>('start')
  const [toast, setToast] = useState<{ kind: 'success' | 'error' | 'info'; message: string } | null>(null)
  const toastTimerRef = useRef<number | null>(null)

  const pushToast = useCallback((message: string, kind: 'success' | 'error' | 'info' = 'info') => {
    setToast({ kind, message })
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current)
    toastTimerRef.current = window.setTimeout(() => setToast(null), 3200)
  }, [])

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current)
        toastTimerRef.current = null
      }
    }
  }, [])

  const connectBadge = useMemo(() => {
    if (!stripeStatus) return 'Loading'
    if (!stripeStatus.stripe_connected) return 'Not connected'
    if (stripeStatus.charges_enabled && stripeStatus.payouts_enabled) return 'Enabled'
    return 'Action required'
  }, [stripeStatus])

  const refreshStripeOnly = useCallback(async () => {
    try {
      const stripeData = await getPayoutsStripeStatus()
      setStripeStatus(stripeData)
    } catch {
      // non-blocking retry path
    }
  }, [])

  const loadSummary = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [stripeData, summaryData, activityData] = await Promise.all([
        getPayoutsStripeStatus(),
        getPayoutsSummary(),
        getPayoutsActivity(1),
      ])
      setStripeStatus(stripeData)
      setSummary(summaryData)
      setActivity(activityData.items || [])
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load payouts page.')
    } finally {
      setLoading(false)
    }
  }, [])

  const handleOnboardingQuery = useCallback(async () => {
    const params = new URLSearchParams(location.search)
    const flow = params.get('onboarding')
    if (!flow) return false

    if (flow === 'refresh') {
      setBusy(true)
      pushToast('Refreshing Stripe setup...', 'info')
      try {
        const { url } = await createPayoutsOnboardingLink()
        window.location.assign(url)
        return true
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || 'Unable to refresh Stripe onboarding link.')
        pushToast('Unable to refresh Stripe setup link.', 'error')
        navigate('/settings/payouts', { replace: true })
        return false
      } finally {
        setBusy(false)
      }
    }

    if (flow === 'return') {
      pushToast('Stripe info submitted. It may take a few minutes to update.', 'success')
      await loadSummary()
      window.setTimeout(() => void refreshStripeOnly(), 5000)
      window.setTimeout(() => void refreshStripeOnly(), 15000)
      navigate('/settings/payouts', { replace: true })
      return true
    }

    if (flow === 'cancelled') {
      pushToast('Stripe setup was cancelled.', 'info')
      navigate('/settings/payouts', { replace: true })
      return false
    }

    return false
  }, [location.search, navigate, loadSummary, refreshStripeOnly, pushToast])

  useEffect(() => {
    void (async () => {
      const handled = await handleOnboardingQuery()
      if (!handled) await loadSummary()
    })()
  }, [handleOnboardingQuery, loadSummary])

  const openStripeModal = useCallback((action: StripeAction) => {
    setModalAction(action)
    setModalOpen(true)
  }, [])

  const continueToStripe = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      const payload = modalAction === 'resume' ? await createPayoutsOnboardingLink() : await startPayoutsConnect()
      window.location.assign(payload.url)
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Unable to start Stripe onboarding.'
      setError(msg)
      pushToast(msg, 'error')
    } finally {
      setBusy(false)
      setModalOpen(false)
    }
  }, [modalAction, pushToast])

  return (
    <div className="space-y-4">
      <StripeRedirectModal
        open={modalOpen}
        busy={busy}
        onCancel={() => setModalOpen(false)}
        onContinue={() => void continueToStripe()}
      />

      {toast ? (
        <div className={`rounded-xl border p-3 text-sm ${toast.kind === 'error' ? 'border-rose-500/30 bg-rose-500/10 text-rose-200' : toast.kind === 'success' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' : 'border-sky-500/30 bg-sky-500/10 text-sky-200'}`}>
          {toast.message}
        </div>
      ) : null}

      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold">Payouts</h1>
          <div className="text-sm text-white/70">Set up Stripe, review earnings, and track payout activity.</div>
        </div>
        <span className="text-xs px-2 py-1 rounded-full border border-white/15 bg-black/20 text-white/80">
          {connectBadge}
        </span>
      </div>

      {error ? <div className="rounded-xl border border-rose-500/25 bg-rose-500/10 p-4 text-sm text-rose-200">{error}</div> : null}

      <StripePayoutSetupCard
        status={stripeStatus}
        busy={busy}
        onConnectStart={() => openStripeModal('start')}
        onResumeSetup={() => openStripeModal('resume')}
      />
      <EarningsSummaryCard summary={summary} />

      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="text-lg font-extrabold">Earnings & payouts</div>
        <div className="mt-1 text-sm text-white/70">Recent earnings and payout activity.</div>
        {loading ? <div className="mt-3 text-sm text-white/70">Loading activity...</div> : null}
        {!loading && !activity.length ? (
          <div className="mt-3 text-sm text-white/70">No earnings yet. Earnings will appear here when you complete bookings or earn subscriptions.</div>
        ) : null}
        {!loading && activity.length ? (
          <ul className="mt-3 space-y-2">
            {activity.map((item) => (
              <li key={`${item.id}:${item.created_at}`} className="rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-white/90">{item.title}</span>
                  <span className={item.amount_usd_cents >= 0 ? 'text-emerald-300' : 'text-amber-200'}>
                    {item.amount_usd_cents >= 0 ? '+' : '-'}{moneyFromCents(Math.abs(item.amount_usd_cents))}
                  </span>
                </div>
                <div className="mt-1 text-xs text-white/60">
                  {item.subtitle ? `${item.subtitle} - ` : ''}{dateLabel(item.created_at)}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  )
}
